import Airtable from "airtable";
import { removeQueryParameters } from "../helpers.js";
import { OPPORTUNITIES_TABLE } from "./opportunityFilterFormulas.js";
import { formatSearchValue, truncatePreview } from "./companyProfileSearch.js";

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base = new Airtable({ apiKey }).base(baseId);

const CACHE_TTL_MS = 90_000;
const cache = new Map();

const parseInterestedGrantNames = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).map((s) => s.trim()).filter(Boolean);
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const escapeAirtableString = (value) => String(value || "").replace(/'/g, "\\'");

const grantIdentifierFor = (grant) =>
  `${grant?.fields?.Name}-${removeQueryParameters(grant?.fields?.GrantUrl)}`;

/**
 * Load Grant Q&A search index for a company (InterestedIn + All Grants).
 * Cached briefly so opening ⌘⇧K repeatedly stays snappy.
 */
export async function loadCompanyGrantQaSearchIndex(company, { force = false } = {}) {
  const companyId = company?.id;
  if (!companyId) {
    return { items: [], interestedCount: 0, allGrantsCount: 0 };
  }

  const interestedKey = parseInterestedGrantNames(company?.fields?.InterestedIn).join("|");
  const cacheKey = `${companyId}::${interestedKey}`;
  const cached = cache.get(cacheKey);
  if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.data;
  }

  const interested = new Set(interestedKey.split("|").filter(Boolean));

  const [answerRecords, questionRecords, grantRecords, opportunityRecords] =
    await Promise.all([
      base("answers").select({ view: "Grid view" }).all(),
      base("questions").select({ view: "Grid view" }).all(),
      base("deduped_grants").select({ view: "Grid view" }).all(),
      base(OPPORTUNITIES_TABLE)
        .select({
          filterByFormula: `{CompanyID} = '${escapeAirtableString(companyId)}'`,
          fields: ["GrantID", "UpgradeName", "CompanyID"],
        })
        .all()
        .catch((err) => {
          console.error("[loadCompanyGrantQaSearchIndex] opportunities error:", err);
          return [];
        }),
    ]);

  const interestedMatched = grantRecords.filter(
    (record) => record.fields.Name && interested.has(record.fields.Name)
  );

  const grantIdCounts = {};
  for (const opp of opportunityRecords || []) {
    const rawGrantId = opp.fields?.GrantID;
    const grantId = Array.isArray(rawGrantId)
      ? rawGrantId[0]
      : rawGrantId
        ? String(rawGrantId).trim()
        : null;
    if (!grantId) continue;
    grantIdCounts[grantId] = (grantIdCounts[grantId] || 0) + 1;
  }

  const grantsById = new Map(grantRecords.map((g) => [g.id, g]));
  const opportunityMatched = Object.keys(grantIdCounts)
    .map((id) => grantsById.get(id))
    .filter((record) => record?.fields?.Name)
    .sort((a, b) =>
      String(a.fields.Name).localeCompare(String(b.fields.Name), undefined, {
        sensitivity: "base",
      })
    );

  const items = [];

  const pushGrantBundle = (grants, matchingMode, modeLabel) => {
    for (const grant of grants) {
      const grantName = grant.fields?.Name || "Untitled grant";
      const grantIdentifier = grantIdentifierFor(grant);

      items.push({
        id: `grant-${matchingMode}-${grant.id}`,
        resultKey: `grant-${matchingMode}-${grant.id}`,
        type: "grant",
        sectionId: "company-grant-qa",
        grantId: grant.id,
        grantName,
        matchingMode,
        title: grantName,
        subtitle: `Grant · ${modeLabel}`,
        keywords: [grantName, modeLabel, "grant"],
        category: "Grants",
      });

      const matchedQuestions = questionRecords.filter(
        (question) =>
          question.fields.grantIdentifier &&
          question.fields.grantIdentifier.split(",").includes(grantIdentifier)
      );

      for (const question of matchedQuestions) {
        const questionText = question.fields?.QuestionText || "";
        const potentialAnswers = answerRecords.filter(
          (ans) =>
            Boolean(ans.fields.AnswerText) &&
            ans.fields.QuestionID === question.fields.QuestionID &&
            ans.fields.grantIdentifier === grantIdentifier &&
            ans.fields.CompanyID === companyId
        );
        const answerText = potentialAnswers[0]?.fields?.AnswerText || "";
        const value = [questionText, answerText].filter(Boolean).join(" ");

        items.push({
          id: `qa-${matchingMode}-${grant.id}-${question.id}`,
          resultKey: `qa-${matchingMode}-${grant.id}-${question.id}`,
          type: "qa",
          sectionId: "company-grant-qa",
          grantId: grant.id,
          grantName,
          matchingMode,
          questionId: question.id,
          questionText,
          answerText,
          title: truncatePreview(questionText, 110) || "Untitled question",
          subtitle: answerText
            ? `${grantName} · ${truncatePreview(answerText, 72)}`
            : `${grantName} · Unanswered · ${modeLabel}`,
          value: formatSearchValue(value),
          keywords: [grantName, modeLabel, "question", "answer", "q&a"],
          category: "Questions & Answers",
        });
      }
    }
  };

  pushGrantBundle(interestedMatched, "interestedIn", "InterestedIn");
  pushGrantBundle(opportunityMatched, "allGrants", "All Grants");

  // Dedupe Q&A rows that appear in both modes: keep InterestedIn copy first,
  // but also keep All Grants–only grants. For shared Qs, prefer InterestedIn.
  const seenQa = new Set();
  const deduped = [];
  for (const item of items) {
    if (item.type !== "qa") {
      deduped.push(item);
      continue;
    }
    const key = `${item.grantId}::${item.questionId}`;
    if (seenQa.has(key)) continue;
    seenQa.add(key);
    deduped.push(item);
  }

  // Dedupe grants similarly (same grant in both modes → keep InterestedIn)
  const seenGrants = new Set();
  const finalItems = [];
  for (const item of deduped) {
    if (item.type !== "grant") {
      finalItems.push(item);
      continue;
    }
    if (seenGrants.has(item.grantId)) continue;
    seenGrants.add(item.grantId);
    finalItems.push(item);
  }

  const data = {
    items: finalItems,
    interestedCount: interestedMatched.length,
    allGrantsCount: opportunityMatched.length,
  };
  cache.set(cacheKey, { at: Date.now(), data });
  return data;
}

export function invalidateCompanyGrantQaSearchIndex(companyId) {
  if (!companyId) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(`${companyId}::`)) cache.delete(key);
  }
}
